import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, PauseIcon as Pause, GraduationCapIcon as GraduationCap, ScissorsIcon as Scissors, FootprintsIcon as Footprints, HouseIcon as House, MapPinIcon as MapPin, ArrowSquareOutIcon as ArrowSquareOut, SparkleIcon as Sparkle, TreeIcon as Tree } from "@phosphor-icons/react";

const KIND_META = {
  trainer: { label: "Trainer", Icon: GraduationCap },
  groomer: { label: "Groomer", Icon: Scissors },
  walker:  { label: "Walker",  Icon: Footprints },
  sitter:  { label: "Sitter",  Icon: House },
  field_host: { label: "Zoomie Time", Icon: Tree },
  other:   { label: "Other",   Icon: Sparkle },
};

const STATUS_META = {
  pending:   { label: "Pending", color: "bg-cream text-[#8A6A00] border-[#E2DAC2]" },
  approved:  { label: "Approved", color: "bg-[#E8F5EE] text-[#0F8043] border-[#0F8043]/30" },
  suspended: { label: "Suspended", color: "bg-[#FFF0F0] text-[#7A1A1A] border-[#C1272D]/30" },
  rejected:  { label: "Rejected", color: "bg-[#FFF0F0] text-[#7A1A1A] border-[#C1272D]/30" },
};

/**
 * Admin curation queue for service providers.
 *
 * Curated-first-100 mode (per PRD): every new signup lands here as
 * `status=pending`. Lauren reviews each one and approves / rejects /
 * suspends. After 100 approved we'll flip a feature flag to open
 * mode (flag check is Phase 2; the queue still works either way).
 *
 * The page intentionally surfaces the legal acknowledgment metadata
 * (signed_at + IP) on the detail drawer because that's the venue
 * moat's first line of defense and admin should be able to confirm
 * it was captured.
 */
export default function AdminServiceProviders() {
  const [rows, setRows] = useState([]);
  const [params] = useSearchParams();
  // Deep links from ops tiles (?kind=field_host) should show ALL
  // statuses for that kind, not just the pending queue.
  const [filter, setFilter] = useState(params.get("status") || (params.get("kind") ? "all" : "pending"));
  const [kind, setKind] = useState(params.get("kind") || "");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (kind) params.kind = kind;
      const r = await api.get("/admin/providers", { params });
      setRows(r.data || []);
    } catch {
      toast.error("Could not load providers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status, opts = {}) => {
    try {
      await api.put(`/admin/providers/${id}/status`, { status, reason: opts.reason || null });
      toast.success(`Set to ${status}`);
      setDetail(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <AppShell>
      <div className="max-w-6xl">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">Admin</p>
        <h1 className="font-spectral font-light text-4xl md:text-5xl tracking-tight leading-[1.05]">
          Service providers
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Curation queue for independent trainers, groomers, walkers, and sitters. Approve to publish them to <code className="font-mono bg-[#FAFAFA] px-1 rounded">/find-pros</code>. Suspend to hide an approved provider without deleting their data.
        </p>

        <div className="gold-hairline my-6 max-w-md" />

        <div className="flex flex-wrap gap-2 mb-4">
          {["pending", "approved", "suspended", "rejected", "all"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              data-testid={`status-filter-${s}`}
              className={`inline-flex items-center text-[12px] font-semibold rounded-full px-3 h-8 transition-colors ${
                filter === s ? "bg-ink text-white" : "bg-white border border-[#EAEAEA] text-ink hover:bg-[#FAFAFA]"
              }`}
            >
              {s === "all" ? "All" : STATUS_META[s].label}
              {counts[s] != null && <span className="ml-1 opacity-70">· {counts[s]}</span>}
            </button>
          ))}
          <Select value={kind} onValueChange={(v) => setKind(v === "any" ? "" : v)}>
            <SelectTrigger className="h-8 w-40" data-testid="kind-filter">
              <SelectValue placeholder="Any kind" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any kind</SelectItem>
              {Object.entries(KIND_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-sm text-[#6B6B6B]">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-[#EAEAEA] rounded-xl p-8 text-center text-sm text-[#6B6B6B]" data-testid="providers-empty">
            No providers in this filter yet.
          </div>
        ) : (
          <div className="bg-white border border-[#EAEAEA] rounded-xl divide-y divide-[#F0F0F0]" data-testid="providers-table">
            {rows.map((p) => {
              const meta = KIND_META[p.kind] || KIND_META.trainer;
              const Icon = meta.Icon;
              const status = STATUS_META[p.status] || STATUS_META.pending;
              return (
                <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`provider-row-${p.id}`}>
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-cream ring-1 ring-[#E2DAC2] flex-shrink-0">
                    <Icon size={18} weight="duotone" className="text-[#8A6A00]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink truncate">{p.business_name}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-[12px] text-[#6B6B6B]">
                      <span>{p.specialty_label || meta.label}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1"><MapPin size={11} weight="duotone" /> {p.location?.city}, {p.location?.state}</span>
                      <span>·</span>
                      <span className="font-mono">{p.email}</span>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${status.color}`}>
                    {status.label}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => setDetail(p)} className="rounded-full" data-testid={`view-${p.id}`}>
                    Review
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail / decision dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="provider-detail-dialog">
          {detail && (() => {
            const meta = KIND_META[detail.kind] || KIND_META.trainer;
            const Icon = meta.Icon;
            const status = STATUS_META[detail.status] || STATUS_META.pending;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-spectral text-2xl flex items-center gap-2">
                    <Icon size={20} weight="duotone" className="text-[#8A6A00]" />
                    {detail.business_name}
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ml-2 ${status.color}`}>
                      {status.label}
                    </span>
                  </DialogTitle>
                </DialogHeader>

                <div className="mt-2 space-y-4 text-[13px]">
                  {detail.tagline && <p className="text-[#6B6B6B] italic">{detail.tagline}</p>}
                  {detail.bio && <p className="whitespace-pre-wrap leading-relaxed">{detail.bio}</p>}

                  <div className="grid grid-cols-2 gap-3">
                    <Box label="Location">{detail.location?.city}, {detail.location?.state} {detail.location?.zip}</Box>
                    <Box label="Contact">
                      <a href={`mailto:${detail.email}`} className="underline hover:text-crimson">{detail.email}</a>
                      {detail.phone && <> · {detail.phone}</>}
                    </Box>
                    {detail.website_url && (
                      <Box label="Website" wide>
                        <a href={detail.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-crimson hover:underline">
                          {detail.website_url} <ArrowSquareOut size={11} />
                        </a>
                      </Box>
                    )}
                    {detail.service_area?.zips?.length > 0 && (
                      <Box label="Service ZIPs" wide>
                        {detail.service_area.zips.join(", ")}
                      </Box>
                    )}
                  </div>

                  {detail.kind === "other" && (
                    <SpecialtyLabelEditor provider={detail} onSaved={(row) => { setDetail(row); load(); }} />
                  )}

                  {detail.kind === "field_host" && detail.land && (
                    <div className="bg-[#F2F8F3] border border-[#2E6B3E]/25 rounded-lg p-3 space-y-1.5" data-testid="admin-land-summary">
                      <p className="font-semibold text-ink text-[12px] flex items-center gap-1.5">
                        <Tree size={13} weight="duotone" className="text-[#2E6B3E]" /> Field details (self-reported)
                      </p>
                      <p className="text-[12px] text-[#3A3A3A]">
                        {detail.land.acres} acres · {detail.land.fenced === "fully" ? `fully fenced${detail.land.fence_height_ft ? ` (${detail.land.fence_height_ft} ft)` : ""}` : detail.land.fenced === "partially" ? "partially fenced" : "not fenced"} · max {detail.land.max_dogs} dogs · {detail.land.buffer_minutes || 30} min gap between visits
                      </p>
                      {detail.land.terrain && <p className="text-[12px] text-[#3A3A3A]"><strong>Terrain:</strong> {detail.land.terrain}</p>}
                      {detail.land.hazards_note && <p className="text-[12px] text-[#8A5A00]"><strong>Hazards:</strong> {detail.land.hazards_note}</p>}
                      <p className="text-[12px] text-[#3A3A3A]"><strong>Exact address (private):</strong> {detail.land.exact_address || "—"}</p>
                      {detail.legal_acks?.land_owner?.signed_at && (
                        <p className="text-[11px] text-[#0F8043] font-semibold">
                          ✓ Land-owner certification signed {detail.legal_acks.land_owner.signed_at.slice(0, 10)} (v{detail.legal_acks.land_owner.version}) · IP {detail.legal_acks.land_owner.ip}
                        </p>
                      )}
                    </div>
                  )}

                  {detail.legal_acks?.independent_business && (
                    <div className="bg-[#FAFAFA] border border-[#EAEAEA] rounded-lg p-3 text-[12px] text-[#6B6B6B]">
                      <p className="font-semibold text-ink mb-1">Legal acknowledgment captured</p>
                      <p>
                        <strong>Independent business:</strong> signed {detail.legal_acks.independent_business.signed_at?.slice(0, 19)} from IP {detail.legal_acks.independent_business.ip || "(unknown)"}
                      </p>
                      <p className="mt-0.5">ToS version: <code className="font-mono">{detail.legal_acks.tos_v}</code></p>
                    </div>
                  )}

                  <InsuranceReviewBlock provider={detail} onChanged={load} />

                  {(detail.kind === "walker" || detail.kind === "sitter" || detail.kind === "other") && (
                    <BackgroundCheckReviewBlock provider={detail} onChanged={load} />
                  )}

                  {detail.status === "pending" && (
                    <div className="space-y-2 pt-1">
                      <Input
                        placeholder="Optional reason (shown to provider on rejection/suspension)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid="status-reason-input"
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button onClick={() => setStatus(detail.id, "approved")} className="rounded-full bg-[#0F8043] hover:bg-[#0F8043]/90 text-white" data-testid="approve-btn">
                          <CheckCircle size={14} weight="fill" className="mr-1.5" /> Approve
                        </Button>
                        <Button onClick={() => setStatus(detail.id, "rejected", { reason })} variant="outline" className="rounded-full border-crimson text-crimson hover:bg-[#FFF0F0]" data-testid="reject-btn">
                          <XCircle size={14} weight="duotone" className="mr-1.5" /> Reject
                        </Button>
                      </div>
                    </div>
                  )}

                  {detail.status === "approved" && (
                    <div className="space-y-2 pt-1">
                      <Input
                        placeholder="Optional suspension reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid="suspend-reason-input"
                      />
                      <div className="flex flex-wrap gap-2">
                        <a
                          href={`/provider/${detail.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[12px] text-crimson hover:underline"
                        >
                          View public page <ArrowSquareOut size={11} />
                        </a>
                        <Button onClick={() => setStatus(detail.id, "suspended", { reason })} variant="outline" className="rounded-full border-[#8A6A00] text-[#8A6A00] hover:bg-cream ml-auto" data-testid="suspend-btn">
                          <Pause size={14} weight="duotone" className="mr-1.5" /> Suspend
                        </Button>
                      </div>
                    </div>
                  )}

                  {(detail.status === "suspended" || detail.status === "rejected") && (
                    <div className="pt-1">
                      <Button onClick={() => setStatus(detail.id, "approved")} className="rounded-full bg-[#0F8043] hover:bg-[#0F8043]/90 text-white" data-testid="reinstate-btn">
                        <CheckCircle size={14} weight="fill" className="mr-1.5" /> Reinstate
                      </Button>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Box({ label, wide, children }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B]">{label}</p>
      <p className="text-ink mt-0.5">{children}</p>
    </div>
  );
}

/**
 * Insurance review: shows the COI doc + admin verify/reject controls.
 * Admin sees fields that aren't exposed publicly (carrier, policy #).
 */
function InsuranceReviewBlock({ provider, onChanged }) {
  const ins = provider.insurance;
  const [busy, setBusy] = useState(false);
  if (!ins?.expires_at) {
    return (
      <div className="bg-[#FAFAFA] border border-[#EAEAEA] rounded-lg p-3 text-[12px] text-[#6B6B6B]">
        <p className="font-semibold text-ink mb-0.5">Insurance</p>
        <p>No COI submitted yet.</p>
      </div>
    );
  }
  const set = async (verified) => {
    setBusy(true);
    try {
      await api.put(`/admin/providers/${provider.id}/insurance`, { verified, notes: null });
      toast.success(verified ? "Insurance verified" : "Verification cleared");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`rounded-lg p-3 text-[12px] border ${ins.verified ? "bg-[#E8F5EE] border-[#0F8043]/30 text-[#0F4F2C]" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="admin-insurance-block">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <p className="font-semibold text-ink">Insurance · {ins.verified ? "VERIFIED" : "PENDING"}</p>
        <div className="flex gap-2">
          {ins.coi_doc_url && (
            <a href={ins.coi_doc_url} target="_blank" rel="noreferrer" className="text-crimson hover:underline inline-flex items-center gap-1" data-testid="admin-coi-link">
              View COI <ArrowSquareOut size={11} />
            </a>
          )}
          {ins.verified ? (
            <Button size="sm" variant="outline" onClick={() => set(false)} disabled={busy} className="h-7 rounded-full" data-testid="unverify-insurance-btn">
              Clear verification
            </Button>
          ) : (
            <Button size="sm" onClick={() => set(true)} disabled={busy} className="h-7 rounded-full bg-[#0F8043] hover:bg-[#0F8043]/90 text-white" data-testid="verify-insurance-btn">
              <CheckCircle size={12} weight="fill" className="mr-1" /> Verify
            </Button>
          )}
        </div>
      </div>
      <p>Carrier: <strong>{ins.carrier || "(none)"}</strong></p>
      <p>Policy #: <strong>{ins.policy_number || "(none)"}</strong></p>
      <p>Expires: <strong>{ins.expires_at}</strong>{ins.coverage_amount ? ` · ${Number(ins.coverage_amount).toLocaleString()} coverage` : ""}</p>
      {ins.verified_at && <p className="text-[11px] mt-1">Verified {ins.verified_at.slice(0, 19)}</p>}
    </div>
  );
}

/**
 * Background-check admin control. Stubbed Checkr workflow — admin
 * manually flips status until live Checkr API access is wired in.
 */
function BackgroundCheckReviewBlock({ provider, onChanged }) {
  const bg = provider.background_check || { status: "not_started" };
  const [busy, setBusy] = useState(false);
  const set = async (status) => {
    setBusy(true);
    try {
      await api.put(`/admin/providers/${provider.id}/background-check`, { status, notes: null });
      toast.success(`Background check set to ${status}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };
  const tone = bg.status === "clear"
    ? "bg-[#E8F5EE] border-[#0F8043]/30"
    : bg.status === "fail"
      ? "bg-[#FFF0F0] border-[#C1272D]/30"
      : "bg-amber-50 border-amber-200";
  return (
    <div className={`rounded-lg p-3 text-[12px] border ${tone}`} data-testid="admin-bg-check-block">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <p className="font-semibold text-ink">Background check · {(bg.status || "not_started").toUpperCase()}</p>
        <div className="flex gap-1.5">
          <Button size="sm" onClick={() => set("clear")} disabled={busy} className="h-7 rounded-full bg-[#0F8043] hover:bg-[#0F8043]/90 text-white" data-testid="bg-set-clear-btn">
            Clear
          </Button>
          <Button size="sm" variant="outline" onClick={() => set("fail")} disabled={busy} className="h-7 rounded-full border-crimson text-crimson hover:bg-[#FFF0F0]" data-testid="bg-set-fail-btn">
            Fail
          </Button>
          <Button size="sm" variant="outline" onClick={() => set("requested")} disabled={busy} className="h-7 rounded-full" data-testid="bg-set-requested-btn">
            Mark requested
          </Button>
        </div>
      </div>
      {bg.paid && (
        <p className="text-[11px] mt-1" data-testid="admin-bg-payment">
          Paid: <strong>${(bg.paid_amount || 39).toFixed(2)}</strong>
          {" · "}
          Credit: <strong className={
            bg.credit_status === "granted" ? "text-[#0F8043]" :
              bg.credit_status === "failed" ? "text-crimson" : ""
          }>
            {bg.credit_status === "granted" ? `$${(bg.credit_amount || 25).toFixed(2)} sent` :
             bg.credit_status === "pending_first_booking" ? "pending first booking" :
             bg.credit_status === "deferred_no_connect" ? "deferred — no Connect" :
             bg.credit_status === "skipped_not_first" ? "skipped (not first)" :
             bg.credit_status === "failed" ? "failed" :
             "n/a"}
          </strong>
        </p>
      )}
      {bg.completed_at && <p className="text-[11px]">Completed {bg.completed_at.slice(0, 19)}</p>}
    </div>
  );
}

function SpecialtyLabelEditor({ provider, onSaved }) {
  const [label, setLabel] = useState(provider.specialty_label || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (label.trim().length < 3) return toast.error("Label must be at least 3 characters.");
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/providers/${provider.id}/specialty-label`, { specialty_label: label.trim() });
      toast.success("Specialty label updated");
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save label");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="bg-cream border border-[#E2DAC2] rounded-lg p-3 space-y-2" data-testid="specialty-label-editor">
      <p className="font-semibold text-ink text-[12px] flex items-center gap-1.5">
        <Sparkle size={13} weight="duotone" className="text-[#8A6A00]" />
        Specialty label (shows instead of "Other" everywhere)
      </p>
      <div className="flex gap-2">
        <Input
          value={label}
          maxLength={40}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Pooper Scooper"
          data-testid="admin-specialty-label-input"
        />
        <Button
          size="sm"
          onClick={save}
          disabled={saving || label.trim() === (provider.specialty_label || "")}
          className="rounded-full whitespace-nowrap"
          data-testid="admin-specialty-label-save"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
      <p className="text-[11px] text-[#6B6B6B]">Clean this up before approving so the public directory stays professional.</p>
    </div>
  );
}
